import {useCallback, useMemo, useState} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import ReactSelect, {components, OptionProps} from 'react-select';
import TableMetronic from '../../../app/common/components/table/TableMetronic';
import {ListService} from '../Types/service';
import {useIntl} from 'react-intl';
import {getStatusColor, getStatusLabel} from '../Helpers/stateHelper';
import {accessorService, getBuColLabel, getServiceColLabelOnly} from '../Helpers/ServiceHelper';
import {createSearchParams, useNavigate} from 'react-router-dom';
import ActionIcon from '../../../Components/ActionIcon';
import {useGetMe} from '../../../app/hooks/useGetMe';
import {isClientCIS} from '../../Auth/Utils/CustomerHelper';
import EtatBadge from './EtatBadge';
import styles from './Services.module.scss';

interface AccountOption {
  value: string;
  buId: number;
  buName: string;
  codeX3: string;
  count: number;
  label: string;
}

interface ServiceOption {
  value: number;
  label: string;
  count: number;
}

const filterSelectStyles = {
  menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 9999}),
};

const CheckboxOption = <T extends {label: string; count?: number}>(props: OptionProps<T, true>) => (
  <components.Option {...props}>
    <div className="d-flex align-items-start">
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        className="me-2 mt-1 flex-shrink-0"
      />
      <label className="mb-0 flex-grow-1" style={{wordBreak: 'break-word'}}>
        {props.label}
      </label>
      {props.data.count != null && (
        <span className="ms-2 text-muted flex-shrink-0">({props.data.count})</span>
      )}
    </div>
  </components.Option>
);

interface DomaineProps {
  isLoading?: boolean;
  services?: ListService[];
  title?: string;
}

const Domaine = ({isLoading, services, title}: DomaineProps): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const customer = useGetMe();
  const [selectedAccounts, setSelectedAccounts] = useState<AccountOption[]>([]);
  const [selectedServices, setSelectedServices] = useState<ServiceOption[]>([]);

  const accountOptions = useMemo<AccountOption[]>(() => {
    if (!services) return [];
    const map = new Map<string, AccountOption>();
    services.forEach((s) => {
      if (s.buId == null) return;
      const buName = s.buName ?? '';
      const codeX3 = s.codeX3 ?? '';
      const key = `${s.buId}|${codeX3}|${buName}`;
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = [s.buId, codeX3, buName].filter((p) => p !== '' && p != null).join(' - ');
      map.set(key, {value: key, buId: s.buId, buName, codeX3, label, count: 1});
    });
    return Array.from(map.values()).sort(
      (a, b) =>
        a.buId - b.buId || a.codeX3.localeCompare(b.codeX3) || a.buName.localeCompare(b.buName)
    );
  }, [services]);

  const serviceOptions = useMemo<ServiceOption[]>(() => {
    if (!services) return [];
    const map = new Map<number, ServiceOption>();
    services.forEach((s) => {
      if (s.serviceId == null) return;
      const existing = map.get(s.serviceId);
      if (existing) {
        existing.count += 1;
        return;
      }
      const label = [s.serviceId, s.serviceName || s.link_name]
        .filter((p) => p !== '' && p != null)
        .join(' - ');
      map.set(s.serviceId, {value: s.serviceId, label, count: 1});
    });
    return Array.from(map.values()).sort((a, b) => a.value - b.value);
  }, [services]);

  const filteredServices = useMemo(() => {
    if (!services) return services;
    if (selectedAccounts.length === 0 && selectedServices.length === 0) return services;
    const accountKeys = new Set(selectedAccounts.map((o) => o.value));
    const serviceIds = new Set(selectedServices.map((o) => o.value));
    return services.filter((s) => {
      const accountOk =
        accountKeys.size === 0 ||
        (s.buId != null && accountKeys.has(`${s.buId}|${s.codeX3 ?? ''}|${s.buName ?? ''}`));
      const serviceOk =
        serviceIds.size === 0 || (s.serviceId != null && serviceIds.has(s.serviceId));
      return accountOk && serviceOk;
    });
  }, [services, selectedAccounts, selectedServices]);

  const getDomaineActionOnClick = useCallback(
    (row: ListService): (() => void) => {
      if (!isClientCIS(customer)) {
        return () =>
          window.open(
            `https://extranet2.stella-telecom.fr/domaine/details/domaine/${row.link_name}`,
            '_blank'
          );
      }
      const searchParams = new URLSearchParams();
      searchParams.append('domaine', row.link_name.toString());
      return () =>
        navigate({
          pathname: `/services/domaine/infos`,
          search: createSearchParams(searchParams).toString(),
        });
    },
    [navigate, customer]
  );

  const renderDomaineAction = useCallback(
    (row: ListService): JSX.Element | null => {
      if (!row.is_domain) return null;
      return (
        <ActionIcon
          label={intl.formatMessage({id: 'SERVICE.DOMAINE.ACTION.DETAILS'})}
          faIcon="fs-1 fa-solid fa-globe"
          onClick={getDomaineActionOnClick(row)}
        />
      );
    },
    [intl, getDomaineActionOnClick]
  );

  const renderDomaineActionButton = useCallback(
    (row: ListService): JSX.Element | null => {
      if (!row.is_domain) return null;
      return (
        <button
          type="button"
          className="btn btn-sm btn-light-primary"
          onClick={getDomaineActionOnClick(row)}
        >
          <i className="fa-solid fa-globe me-2" />
          {intl.formatMessage({id: 'SERVICE.DOMAINE.ACTION.DETAILS'})}
        </button>
      );
    },
    [intl, getDomaineActionOnClick]
  );

  const columns = useMemo<ColumnDef<ListService>[]>(
    () => [
      {
        header: intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.ID_SERVICE'}),
        accessorKey: 'serviceId',
        size: 110,
        cell: (info) => info.getValue<number | null>() ?? '-',
      },
      {
        header: intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.ACCOUNT'}),
        accessorKey: 'buId',
        cell: getBuColLabel,
        size: 100,
      },
      {
        header: intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.SERVICE'}),
        accessorKey: 'service',
        cell: getServiceColLabelOnly,
        accessorFn: accessorService,
        sortingFn: (rowA, rowB) => {
          const idA = rowA.original.serviceId ?? 0;
          const idB = rowB.original.serviceId ?? 0;
          return idA - idB;
        },
      },
      {
        header: intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.STATE'}),
        accessorKey: 'state',
        size: 85,
        cell: (info) => {
          const status = info.row.original.state;
          const color = getStatusColor(status);
          return status ? <EtatBadge color={color} label={getStatusLabel(status, intl)} /> : '-';
        },
      },
      // {
      //   header: 'Site',
      //   accessorKey: 'siteId',
      //   cell: getSiteColLabel,
      //   accessorFn: accessorSite,
      //   size: 250,
      //   sortingFn: (rowA, rowB) => {
      //     const idA = rowA.original.site?.id ?? 0;
      //     const idB = rowB.original.site?.id ?? 0;
      //     return idA - idB;
      //   },
      // },
      {
        header: intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.ACTIONS'}),
        accessorKey: 'domaine',
        size: 90,
        cell: (info) => renderDomaineAction(info.row.original),
      },
    ],
    [intl, renderDomaineAction]
  );

  const renderMobileCard = (row: ListService): JSX.Element => {
    const status = row.state;

    return (
      <div className={styles.mobileCardBody}>
        <div className={styles.mobileCardHeader}>
          <span style={{minWidth: 0, wordBreak: 'break-word'}}>
            {getServiceColLabelOnly({row: {original: row}})}
          </span>
          {status && (
            <span className="flex-shrink-0">
              <EtatBadge color={getStatusColor(status)} label={getStatusLabel(status, intl)} />
            </span>
          )}
        </div>
        <div className={styles.mobileCardRow}>
          <span className={styles.mobileCardLabel}>
            {intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.ACCOUNT'})}
          </span>
          {getBuColLabel({row: {original: row}})}
        </div>
        <div className={styles.mobileCardRow}>
          <span className={styles.mobileCardLabel}>
            {intl.formatMessage({id: 'SERVICE.DOMAINE.TABLE.COLUMN.ID_SERVICE'})}
          </span>
          <span>{row.serviceId ?? '-'}</span>
        </div>
        {row.is_domain && <div className="mt-1">{renderDomaineActionButton(row)}</div>}
      </div>
    );
  };

  const filtersToolbar = (
    <>
      <div className={styles.filterSelectWrapper}>
        <ReactSelect<AccountOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={accountOptions}
          value={selectedAccounts}
          onChange={(vals) => setSelectedAccounts(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_ACCOUNTS'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
      <div className={styles.filterSelectWrapper}>
        <ReactSelect<ServiceOption, true>
          isMulti
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          components={{Option: CheckboxOption}}
          options={serviceOptions}
          value={selectedServices}
          onChange={(vals) => setSelectedServices(vals ? [...vals] : [])}
          placeholder={intl.formatMessage({id: 'SERVICES.FILTER.ALL_SERVICES'})}
          noOptionsMessage={() => intl.formatMessage({id: 'FIELD.NO.OPTIONS'})}
          classNamePrefix="filter-select"
          menuPortalTarget={document.body}
          styles={filterSelectStyles}
        />
      </div>
    </>
  );

  return (
    <TableMetronic<ListService>
      toolbar={filtersToolbar}
      stackedHeader
      title={title ?? intl.formatMessage({id: 'MENU.CLOUD.DOMAINS'})}
      data={filteredServices ?? []}
      columns={columns}
      renderMobileCard={renderMobileCard}
      isLoading={isLoading}
      defaultSorting={[{id: 'serviceId', desc: true}]}
      searchBar
      pagination
    />
  );
};

export default Domaine;
